import React, { useState } from 'react';
import { Plus, Trash2, GripVertical, ChevronUp, ChevronDown, Image as ImageIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import RichTextEditor from './RichTextEditor';
import MediaPicker from '../MediaPicker';
import { resolveMediaUrl } from '../../utils/mediaUrl';

const BLOCK_TYPES = [
  { value: 'heading',  label: 'Encabezado (eyebrow + título + subtítulo)' },
  { value: 'richtext', label: 'Texto rico (párrafos con WYSIWYG)' },
  { value: 'quote',    label: 'Cita destacada' },
  { value: 'gallery',  label: 'Galería de imágenes' },
  { value: 'image',    label: 'Imagen única con caption' },
  { value: 'cta',      label: 'Call to action (título + texto + botón)' },
];

const emptyVersion = (country) => ({
  countryCode: country.code,
  language: country.language,
  heading: '',
  subheading: '',
  body: '',
  caption: '',
  ctaLabel: '',
  ctaUrl: '',
});

const emptySection = (enabledCountries, order) => ({
  key: '',
  blockType: 'richtext',
  images: [],
  order,
  isActive: true,
  versions: enabledCountries.map(emptyVersion),
});

/**
 * Preset section keys shown in an autocomplete-style dropdown so admins pick
 * the same "tag" the frontend renders. Free-form input is still allowed for
 * future custom sections.
 */
const KEY_PRESETS = [
  { key: 'home.manifest.header',   label: 'Home · Manifiesto · Encabezado' },
  { key: 'home.manifest.body',     label: 'Home · Manifiesto · Cuerpo' },
  { key: 'home.split.header',      label: 'Home · Split editorial · Encabezado' },
  { key: 'home.split.gallery',     label: 'Home · Split editorial · Galería' },
  { key: 'home.split.inset',       label: 'Home · Split editorial · Imagen inset' },
  { key: 'home.behind.header',     label: 'Home · Detrás de cada pieza · Encabezado' },
  { key: 'home.behind.gallery',    label: 'Home · Detrás de cada pieza · Galería' },
  { key: 'home.quote.main',        label: 'Home · Cita editorial' },
  { key: 'home.featured.header',   label: 'Home · Piezas destacadas · Encabezado' },
  { key: 'home.newsletter.cta',    label: 'Home · Newsletter CTA' },
  { key: 'about.hero.header',      label: 'About · Hero · Encabezado' },
  { key: 'about.origin.story',     label: 'About · Origen' },
  { key: 'about.values.pillars',   label: 'About · Valores' },
  { key: 'about.process.gallery',  label: 'About · Proceso · Galería' },
  { key: 'about.cta.contact',      label: 'About · CTA · Contacto' },
];

const SectionsBuilder = ({ sections = [], enabledCountries = [], onChange }) => {
  const [mediaPickerFor, setMediaPickerFor] = useState(null); // {sectionIdx, imageIdx?}

  const update = (idx, patch) => {
    const next = sections.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    onChange(next);
  };

  const updateVersion = (idx, versionIdx, patch) => {
    const next = sections.map((s, i) => {
      if (i !== idx) return s;
      const versions = s.versions.map((v, j) => (j === versionIdx ? { ...v, ...patch } : v));
      return { ...s, versions };
    });
    onChange(next);
  };

  const addSection = () => {
    const order = sections.length ? Math.max(...sections.map(s => s.order || 0)) + 10 : 10;
    onChange([...sections, emptySection(enabledCountries, order)]);
  };

  const removeSection = (idx) => {
    if (!window.confirm('¿Eliminar esta sección? No se puede deshacer.')) return;
    onChange(sections.filter((_, i) => i !== idx));
  };

  const moveSection = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= sections.length) return;
    const next = [...sections];
    [next[idx], next[target]] = [next[target], next[idx]];
    // Renumber order fields to reflect visual position.
    next.forEach((s, i) => { s.order = (i + 1) * 10; });
    onChange(next);
  };

  const addImage = (idx, url) => {
    const s = sections[idx];
    update(idx, { images: [...(s.images || []), { url, alt: '', caption: '' }] });
  };

  const updateImage = (idx, imgIdx, patch) => {
    const s = sections[idx];
    const images = s.images.map((im, j) => (j === imgIdx ? { ...im, ...patch } : im));
    update(idx, { images });
  };

  const removeImage = (idx, imgIdx) => {
    const s = sections[idx];
    update(idx, { images: s.images.filter((_, j) => j !== imgIdx) });
  };

  // Ensure each section has one version per enabled country when countries change.
  React.useEffect(() => {
    let mutated = false;
    const next = sections.map(s => {
      const versions = enabledCountries.map(c => {
        const existing = (s.versions || []).find(v => v.countryCode === c.code);
        return existing || emptyVersion(c);
      });
      if (versions.length !== (s.versions || []).length) mutated = true;
      return { ...s, versions };
    });
    if (mutated) onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledCountries.length]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Secciones editables</h2>
          <p className="text-sm text-gray-500">
            Bloques que el frontend renderiza en posiciones específicas. Usa la clave (
            <code className="text-xs bg-gray-100 px-1 rounded">home.manifest.body</code>) como "tag" de posición.
          </p>
        </div>
        <Button type="button" onClick={addSection} data-testid="section-add-btn">
          <Plus className="h-4 w-4 mr-2" /> Añadir sección
        </Button>
      </div>

      {sections.length === 0 ? (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-500">
          Aún no hay secciones. Añade tu primera con el botón de arriba.
        </div>
      ) : (
        <Accordion type="multiple" className="space-y-2">
          {sections.map((section, idx) => (
            <AccordionItem
              key={`section-${idx}`}
              value={`section-${idx}`}
              className="border rounded-lg"
              data-testid={`section-${idx}`}
            >
              <div className="flex items-center gap-2 px-3">
                <GripVertical className="h-4 w-4 text-gray-400" />
                <AccordionTrigger className="flex-1 py-3 hover:no-underline">
                  <div className="flex items-center gap-3 flex-1 text-left">
                    <span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded">
                      {section.key || '(sin key)'}
                    </span>
                    <span className="text-sm text-gray-600">{section.blockType}</span>
                    {!section.isActive && (
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Inactiva</span>
                    )}
                  </div>
                </AccordionTrigger>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); moveSection(idx, -1); }}
                  disabled={idx === 0}
                  className="p-1 disabled:opacity-30"
                  aria-label="Subir"
                  data-testid={`section-${idx}-up`}
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); moveSection(idx, 1); }}
                  disabled={idx === sections.length - 1}
                  className="p-1 disabled:opacity-30"
                  aria-label="Bajar"
                  data-testid={`section-${idx}-down`}
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeSection(idx); }}
                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                  aria-label="Eliminar"
                  data-testid={`section-${idx}-remove`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <AccordionContent className="px-4 pt-2 pb-4 space-y-4">
                {/* Row 1: key + block type + active */}
                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <Label>Clave (tag) *</Label>
                    <Input
                      value={section.key}
                      list={`presets-${idx}`}
                      onChange={(e) => update(idx, { key: e.target.value })}
                      placeholder="home.manifest.body"
                      data-testid={`section-${idx}-key`}
                    />
                    <datalist id={`presets-${idx}`}>
                      {KEY_PRESETS.map(p => (
                        <option key={p.key} value={p.key}>{p.label}</option>
                      ))}
                    </datalist>
                  </div>
                  <div>
                    <Label>Tipo de bloque</Label>
                    <Select value={section.blockType} onValueChange={(v) => update(idx, { blockType: v })}>
                      <SelectTrigger data-testid={`section-${idx}-type`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {BLOCK_TYPES.map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm select-none">
                      <Checkbox
                        checked={section.isActive}
                        onCheckedChange={(v) => update(idx, { isActive: Boolean(v) })}
                        data-testid={`section-${idx}-active`}
                      />
                      Activa (visible en la web)
                    </label>
                  </div>
                </div>

                {/* Images / gallery */}
                {(section.blockType === 'gallery' || section.blockType === 'image') && (
                  <div className="border border-gray-200 rounded-lg p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Imágenes ({section.images?.length || 0})</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setMediaPickerFor({ sectionIdx: idx })}
                        data-testid={`section-${idx}-add-image`}
                      >
                        <ImageIcon className="h-4 w-4 mr-2" /> Añadir imagen
                      </Button>
                    </div>
                    {(section.images || []).length === 0 ? (
                      <div className="text-xs text-gray-400 italic">Sin imágenes.</div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {section.images.map((img, imgIdx) => (
                          <div key={imgIdx} className="border rounded-md p-2 space-y-1">
                            <div className="relative aspect-[3/4] bg-gray-100 rounded overflow-hidden">
                              <img
                                src={resolveMediaUrl(img.url)}
                                alt={img.alt || ''}
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => removeImage(idx, imgIdx)}
                                className="absolute top-1 right-1 bg-white/90 rounded-full p-1"
                                aria-label="Quitar imagen"
                                data-testid={`section-${idx}-image-${imgIdx}-remove`}
                              >
                                <Trash2 className="h-3 w-3 text-red-600" />
                              </button>
                            </div>
                            <Input
                              value={img.alt || ''}
                              onChange={(e) => updateImage(idx, imgIdx, { alt: e.target.value })}
                              placeholder="Alt text"
                              className="text-xs h-8"
                            />
                            <Input
                              value={img.caption || ''}
                              onChange={(e) => updateImage(idx, imgIdx, { caption: e.target.value })}
                              placeholder="Caption"
                              className="text-xs h-8"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Language versions */}
                <div className="space-y-3">
                  <Label>Contenido por idioma</Label>
                  <Accordion type="multiple" defaultValue={section.versions.map((_, i) => `v-${idx}-${i}`)}>
                    {section.versions.map((version, vIdx) => {
                      const country = enabledCountries.find(c => c.code === version.countryCode);
                      const flag = country ? country.code : '🌍';
                      return (
                        <AccordionItem key={vIdx} value={`v-${idx}-${vIdx}`} className="border rounded-md mb-2">
                          <AccordionTrigger className="px-3 py-2 hover:no-underline text-sm">
                            <span className="font-medium">
                              [{flag}] {country?.languageName || version.language}
                            </span>
                          </AccordionTrigger>
                          <AccordionContent className="px-3 pb-3 space-y-3">
                            {section.blockType !== 'richtext' && section.blockType !== 'quote' && (
                              <>
                                <div>
                                  <Label className="text-xs">Título / eyebrow</Label>
                                  <Input
                                    value={version.heading || ''}
                                    onChange={(e) => updateVersion(idx, vIdx, { heading: e.target.value })}
                                    data-testid={`section-${idx}-v-${vIdx}-heading`}
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Subtítulo (permite &lt;em&gt; para itálicas)</Label>
                                  <Input
                                    value={version.subheading || ''}
                                    onChange={(e) => updateVersion(idx, vIdx, { subheading: e.target.value })}
                                    data-testid={`section-${idx}-v-${vIdx}-subheading`}
                                  />
                                </div>
                              </>
                            )}
                            {(section.blockType === 'richtext' || section.blockType === 'cta' || section.blockType === 'quote') && (
                              <div>
                                <Label className="text-xs">Cuerpo (WYSIWYG)</Label>
                                <RichTextEditor
                                  value={version.body || ''}
                                  onChange={(v) => updateVersion(idx, vIdx, { body: v })}
                                  dataTestId={`section-${idx}-v-${vIdx}-body`}
                                />
                              </div>
                            )}
                            {(section.blockType === 'quote' || section.blockType === 'image') && (
                              <div>
                                <Label className="text-xs">Caption / cita</Label>
                                <Input
                                  value={version.caption || ''}
                                  onChange={(e) => updateVersion(idx, vIdx, { caption: e.target.value })}
                                />
                              </div>
                            )}
                            {section.blockType === 'cta' && (
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label className="text-xs">Etiqueta del botón</Label>
                                  <Input
                                    value={version.ctaLabel || ''}
                                    onChange={(e) => updateVersion(idx, vIdx, { ctaLabel: e.target.value })}
                                    data-testid={`section-${idx}-v-${vIdx}-cta-label`}
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">URL del botón</Label>
                                  <Input
                                    value={version.ctaUrl || ''}
                                    onChange={(e) => updateVersion(idx, vIdx, { ctaUrl: e.target.value })}
                                    placeholder="/contact-us"
                                  />
                                </div>
                              </div>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      {mediaPickerFor !== null && (
        <MediaPicker
          isOpen
          onClose={() => setMediaPickerFor(null)}
          onSelect={(url) => {
            addImage(mediaPickerFor.sectionIdx, resolveMediaUrl(url));
            setMediaPickerFor(null);
          }}
          fileType="image"
        />
      )}
    </div>
  );
};

export default SectionsBuilder;
